import React, { memo, useRef, useEffect, Fragment } from "react";
import { createPortal } from "react-dom";
import { useBoundStore } from "@maxis/client-logic/store/useBoundStore";
import useMessageBox from "@maxis/client-logic/hooks/useMessageBox";
import styles from "./MessageBox.module.css";

export default function MessageBox() {
  const root = document.getElementById("message-box");
  // store stack
  const stack = useBoundStore((s) => s.messageBox.stack);
  // get `popMessage` from the useMessageBox hook
  const { popMessage } = useMessageBox();

  return createPortal(
    <>
      {stack.map((msg) => (
        <MessageBoxItem
          key={msg.id}
          {...msg}
          onClose={(result) => {
            // when close modal, remove id of stack
            popMessage(msg.id);
            msg.responseHandler(result);
          }}
        />
      ))}
    </>,
    root
  );
}

const MessageBoxItem = memo(({ id, type, title, description, time, onClose }) => {
  const timerRef = useRef();
  const dialogRef = useRef();

  // 언마운트 정리에서 부르는 close() 도 네이티브 close 이벤트를 쏜다.
  // 그대로 두면 사용자가 확인을 누른 뒤 onClose(false) 가 한 번 더 실행된다
  // (지금은 responseHandler 가 promise resolve 라 티가 안 날 뿐이다).
  const unmountingRef = useRef(false);

  // 브라우저 native modal API: 자동으로 dialog에 focus를 옮기고, 이전 focus(active element)는 해제되며,
  // close() 시 원래 focus였던 요소로 자동 복원됨
  useEffect(() => {
    dialogRef.current?.showModal();
    return () => {
      unmountingRef.current = true;
      dialogRef.current?.close();
    };
  }, []);

  // auto-close
  useEffect(() => {
    if (time) {
      timerRef.current = setTimeout(() => onClose(true), time);
    }
    return () => clearTimeout(timerRef.current);
  }, [time, onClose]);

  return (
    <>
      {/* Esc 로 닫으면 브라우저가 dialog 만 닫고 React state 는 그대로다 → 네이티브 close 를
          받아 취소로 처리한다. 단 언마운트 정리에서 온 close 는 무시한다. */}
      <dialog
        ref={dialogRef}
        className={styles.dialog}
        onClose={() => {
          if (!unmountingRef.current) onClose(false);
        }}
        /* ::backdrop 영역 클릭은 target 이 dialog 자신으로 잡힌다.
           padding 안쪽 클릭과 구분하기 위해 좌표로 판정한다. */
        onClick={(e) => {
          if (e.target !== dialogRef.current) return;
          const r = dialogRef.current.getBoundingClientRect();
          const outside =
            e.clientX < r.left ||
            e.clientX > r.right ||
            e.clientY < r.top ||
            e.clientY > r.bottom;
          if (outside) onClose(false);
        }}
      >
        <h2 className={styles[type] || styles.dialogTitle}>{title}</h2>
        {description && (
          <div className={styles.dialogDescription}>
            {typeof description === "string"
              ? description.split("\n").map((line, idx) => (
                  <Fragment key={idx}>
                    {line}
                    <br />
                  </Fragment>
                ))
              : description}
          </div>
        )}
        <div className={styles.dialogButtons}>
          <button
            type="button"
            className={`${styles.dialogButton} ${styles.dialogButtonConfirm}`}
            onClick={() => onClose(true)}
            autoFocus
          >
            확인
          </button>
          {type !== "alert" && type !== "error" && (
            <button
              type="button"
              className={`${styles.dialogButton} ${styles.dialogButtonCancel}`}
              onClick={() => onClose(false)}
            >
              취소
            </button>
          )}
        </div>
      </dialog>
    </>
  );
});
MessageBoxItem.displayName = "MessageBoxItem";
